/**
 * Individual chat message component.
 */

import React, { useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { C1Component, ThemeProvider } from '@thesysai/genui-sdk';
import { CarMarkdown } from '@app/components/Markdown';
import type { ChatMessage as ChatMessageType } from '../types';
import type { C1Action } from '../types/c1Types';
import { extractC1Components, removeC1Tags } from '../utils/componentHelpers';
import {
  RETRIEVED_DATA_REGEX,
  RETRIEVED_DATA_LINE_REGEX,
  JSON_CODE_BLOCK_REGEX,
  RAW_DATA_MARKER_REGEX,
} from '../constants/chatConstants';
import { ChatFeedbackPrompt } from '../ChatFeedbackPrompt';

/**
 * Error boundary to catch errors from C1Component SDK
 */
class C1ComponentErrorBoundary extends Component<
  { 
    children: ReactNode; 
    c1ResponseString: string;
    componentIndex: number;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; c1ResponseString: string; componentIndex: number }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ChatMessage] ❌ C1Component SDK Error (component ${this.props.componentIndex}):`, {
      error,
      errorMessage: error.message,
      errorStack: error.stack,
      componentInfo: errorInfo.componentStack,
      c1ResponsePreview: this.props.c1ResponseString.substring(0, 500),
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <div>Error while generating response</div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              {this.state.error.message}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper to pass props to C1Component and catch errors
 */
const C1ComponentWrapper: React.FC<{
  c1ResponseString: string;
  isStreaming: boolean;
  onAction: (action: C1Action) => void;
  componentIndex: number;
}> = ({ c1ResponseString, isStreaming, onAction, componentIndex }) => {
  return (
    <C1ComponentErrorBoundary c1ResponseString={c1ResponseString} componentIndex={componentIndex}>
      <C1Component
        c1Response={c1ResponseString}
        isStreaming={isStreaming}
        onAction={onAction}
      />
    </C1ComponentErrorBoundary>
  );
};

export interface ChatMessageProps {
  message: ChatMessageType;
  onC1Action: (action: C1Action) => void;
  onFeedbackRendered?: (requestId: string) => void;
  renderedFeedbackRequestIds: Set<string>;
  planResultRootId: string;
  planResultTypeId: string;
  userQuery?: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onC1Action,
  onFeedbackRendered,
  renderedFeedbackRequestIds,
  planResultRootId,
  planResultTypeId,
  userQuery,
}) => {
  const isAssistant = message.role === 'assistant';
  
  // Use c1Components from API response (preferred) or extract from content (fallback)
  // CRITICAL: Keep components in their original format (string or object)
  // C1Component SDK accepts both formats, so don't convert objects to strings
  const c1ComponentsFromApi = (message.c1Components && message.c1Components.length > 0)
    ? message.c1Components.map((comp) => {
        // Log component format for debugging
        if (message.id.includes('assistant')) {
          console.log(`[ChatMessage] 🔍 Processing component for message ${message.id}:`, {
            type: typeof comp,
            isString: typeof comp === 'string',
            isObject: typeof comp === 'object',
            preview: typeof comp === 'string' 
              ? comp.substring(0, 100)
              : JSON.stringify(comp).substring(0, 100),
          });
        }
        // Keep component in original format - C1Component SDK accepts both string and object
        // Only convert to string if it's not already a valid format
        // But actually, let's keep objects as objects since SDK accepts them
        return comp;
      })
    : [];
  const c1ComponentsFromContent = (isAssistant && c1ComponentsFromApi.length === 0)
    ? extractC1Components(message.content)
    : [];
  const c1Components = c1ComponentsFromApi.length > 0 
    ? c1ComponentsFromApi 
    : c1ComponentsFromContent;
  const hasC1Components = isAssistant && c1Components.length > 0;
  
  // Debug: Log final component format
  if (isAssistant && hasC1Components) {
    const firstComponent = c1Components[0];
    console.log(`[ChatMessage] ✅ Final components for message ${message.id}:`, {
      count: c1Components.length,
      allStrings: c1Components.every(c => typeof c === 'string'),
      firstComponentType: typeof firstComponent,
      firstComponentPreview: typeof firstComponent === 'string' 
        ? firstComponent.substring(0, 200)
        : JSON.stringify(firstComponent).substring(0, 200),
    });
  }
  
  let textContent = isAssistant
    ? removeC1Tags(message.content)
    : message.content;
  
  // Remove raw tool result data from text content when C1 components are present
  if (isAssistant && hasC1Components && textContent) {
    textContent = textContent.replace(RETRIEVED_DATA_REGEX, '').trim();
    textContent = textContent.replace(RETRIEVED_DATA_LINE_REGEX, '').trim();
    textContent = textContent.replace(JSON_CODE_BLOCK_REGEX, '').trim();
    if (textContent.match(RAW_DATA_MARKER_REGEX) && textContent.length < 50) {
      textContent = '';
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
        width: '100%',
      }}
    >
      {/* Text content in chat bubble */}
      {(textContent || message.role === 'user') && textContent.trim() && (
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            maxWidth: '80%',
            bgcolor: message.role === 'user' ? 'primary.main' : 'white',
            color: message.role === 'user' ? 'white' : 'text.primary',
            mb: hasC1Components ? 1 : 0,
          }}
        >
          {message.role === 'assistant' ? (
            <>
              {textContent && <CarMarkdown>{textContent}</CarMarkdown>}
              {message.toolCalls && message.toolCalls.length > 0 && (
                <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
                  Used {message.toolCalls.length} tool
                  {message.toolCalls.length > 1 ? 's' : ''}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="par02Regular" sx={{ color: 'white' }}>
              {message.content}
            </Typography>
          )}
        </Paper>
      )}

      {/* C1 components rendered full-width */}
      {message.role === 'assistant' && hasC1Components && (
        <Box
          sx={{
            width: '100%',
            mt: textContent ? 1 : 0,
            mb: 2,
            '& .c1-component, & > *': {
              width: '100%',
            },
          }}
        >
          <ThemeProvider>
            {c1Components.map((c1Response, index) => {
              // Components should already be strings (converted when storing from COMPLETE)
              // But handle both cases for safety
              const c1ResponseString = typeof c1Response === 'string' 
                ? c1Response 
                : JSON.stringify(c1Response);
              
              // Log format for debugging
              if (message.id.includes('assistant')) {
                console.log(`[ChatMessage] 🔄 Component ${index} format:`, {
                  originalType: typeof c1Response,
                  finalType: typeof c1ResponseString,
                  isString: typeof c1ResponseString === 'string',
                  preview: typeof c1ResponseString === 'string' 
                    ? c1ResponseString.substring(0, 150)
                    : 'NOT A STRING!',
                });
              }
              
              const response = c1Response as unknown;
              const responseObj = response as { component?: { component?: string } | string };
              let componentId: string;
              
              if (typeof responseObj === 'object' && responseObj !== null) {
                // Try to extract component ID from object structure
                if ('component' in responseObj) {
                  const comp = responseObj.component;
                  if (typeof comp === 'object' && comp !== null && 'component' in comp) {
                    componentId = (comp as { component?: string }).component || `c1-${index}`;
                  } else if (typeof comp === 'string') {
                    componentId = comp;
                  } else {
                    componentId = `c1-${index}`;
                  }
                } else {
                  componentId = `c1-${index}`;
                }
              } else {
                componentId = `c1-${index}`;
              }
              
              const componentKey = `c1-${String(componentId)}-${index}`;
              
              return (
                <Box
                  key={componentKey}
                  sx={{
                    mb: index < c1Components.length - 1 ? 2 : 0,
                  }}
                >
                  {(() => {
                    try {
                      // Verify conversion worked
                      if (typeof c1ResponseString !== 'string') {
                        console.error(`[ChatMessage] ❌ Component ${index} is not a string!`, {
                          type: typeof c1ResponseString,
                          value: c1ResponseString,
                        });
                        return <div>Error: Component format invalid</div>;
                      }
                      
                      // Try to parse to verify it's valid JSON and check structure
                      let parsedComponent;
                      try {
                        parsedComponent = JSON.parse(c1ResponseString);
                        console.log(`[ChatMessage] ✅ Component ${index} parsed successfully:`, {
                          hasComponent: 'component' in parsedComponent,
                          componentType: parsedComponent?.component,
                          hasProps: 'props' in parsedComponent,
                          structure: Object.keys(parsedComponent || {}),
                        });
                      } catch (parseError) {
                        console.error(`[ChatMessage] ❌ Component ${index} is not valid JSON!`, {
                          error: parseError,
                          preview: c1ResponseString.substring(0, 200),
                        });
                        return <div>Error: Component JSON invalid</div>;
                      }
                      
                      // Log the full component string being passed to SDK (first 500 chars)
                      console.log(`[ChatMessage] 📤 Passing to C1Component SDK (first 500 chars):`, 
                        c1ResponseString.substring(0, 500));
                      
                      // Wrap C1Component to catch any errors it might throw
                      return (
                        <C1ComponentWrapper
                          c1ResponseString={c1ResponseString}
                          isStreaming={message.isStreaming ?? false}
                          onAction={onC1Action}
                          componentIndex={index}
                        />
                      );
                    } catch (error) {
                      console.error(`[ChatMessage] ❌ Error rendering component ${index}:`, error, {
                        component: c1ResponseString,
                        errorMessage: error instanceof Error ? error.message : String(error),
                        errorStack: error instanceof Error ? error.stack : undefined,
                      });
                      return <div>Error while generating response</div>;
                    }
                  })()}
                </Box>
              );
            })}
          </ThemeProvider>
        </Box>
      )}

      {/* Feedback prompt */}
      {message.role === 'assistant' &&
        message.feedbackEnabled &&
        message.feedbackRequestId && (
          <FeedbackRenderer
            requestId={message.feedbackRequestId}
            renderedFeedbackRequestIds={renderedFeedbackRequestIds}
            onFeedbackRendered={onFeedbackRendered}
            planResultRootId={planResultRootId}
            planResultTypeId={planResultTypeId}
            userQuery={userQuery}
            response={message.content}
          />
        )}
    </Box>
  );
};

/**
 * Component that renders feedback prompt only once per requestId.
 * Uses a ref to track if we've checked/rendered, avoiding Set checks during render.
 */
const FeedbackRenderer: React.FC<{
  requestId: string;
  renderedFeedbackRequestIds: Set<string>;
  onFeedbackRendered?: (requestId: string) => void;
  planResultRootId: string;
  planResultTypeId: string;
  userQuery?: string;
  response: string;
}> = ({ 
  requestId, 
  renderedFeedbackRequestIds, 
  onFeedbackRendered,
  planResultRootId,
  planResultTypeId,
  userQuery,
  response,
}) => {
  const hasCheckedRef = React.useRef(false);
  const [shouldRender, setShouldRender] = React.useState(true);
  
  useEffect(() => {
    // Only check once per component instance
    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true;
      // Check if already rendered by another instance
      if (renderedFeedbackRequestIds.has(requestId)) {
        setShouldRender(false);
        return;
      }
      // Mark as rendered
      renderedFeedbackRequestIds.add(requestId);
      onFeedbackRendered?.(requestId);
    }
  }, [requestId, renderedFeedbackRequestIds, onFeedbackRendered]);
  
  if (!shouldRender) {
    return null;
  }
  
  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      <ChatFeedbackPrompt
        planResultRootId={planResultRootId}
        planResultTypeId={planResultTypeId}
        requestId={requestId}
        query={userQuery}
        response={response}
      />
    </Box>
  );
};

